import type { Node } from 'react'
import React from 'react'
import { Link } from 'app/common/link'
import styled, { css } from 'styled-components'
import { fonts, H6, SmallText, Text } from 'app/common/typography'
import { media } from 'app/assets/styles'
import { colors } from 'shared/assets/styles'
import type { EventMetadata } from 'shared/store/metadata'
import { Picture } from 'app/common/picture'
import { categoryId, productFilters } from 'shared/utils'

type Props = {
    actionUrl?: string,
    image: Array<string>,
    name: string,
    backgroundColor?: string,
    color?: string,
    margin?: string,
    priceNode?: Node,
    index?: number,
    meta?: Array<EventMetadata> | EventMetadata,
    iconTop?: Node,
    iconBottom?: Node,
    type?: string,
    tileText?: string,
    hasTileText?: boolean,
    isCategory?: boolean,
    contains: Array<typeof productFilters>
}

const TileSquare = (props: Props) => {
    const {
        actionUrl,
        image,
        name,
        backgroundColor,
        color,
        margin,
        priceNode,
        index,
        meta,
        iconTop,
        iconBottom,
        type,
        tileText,
        hasTileText,
        isCategory,
        contains
    } = props

    let eCommerceProductClickMetadata
    if (type === 'product') {
        eCommerceProductClickMetadata = {
            data: {
                name: name,
                position: index
            },
            type: 'product_click'
        }
    }

    return (
        <Link
            url={actionUrl}
            meta={meta}
            eCommerceMeta={eCommerceProductClickMetadata}>
            <Square backgroundColor={backgroundColor} margin={margin}>
                <StyledH6 color={color} isCategory={isCategory}>
                    {name}
                </StyledH6>
                {iconTop && <IconTop>{iconTop}</IconTop>}
                <SquareContainer>
                    <ImageContainer hasTileText={hasTileText}>
                        <Picture
                            singleImage
                            imageUrl={image}
                            imageTitle={name}
                        />
                    </ImageContainer>
                </SquareContainer>

                {priceNode && <StyledText>{priceNode}</StyledText>}
                {iconBottom && <IconBottom>{iconBottom}</IconBottom>}
                {tileText && (
                    <BottomTextContainer>
                        <SmallText color={colors.grey}>{tileText}</SmallText>
                    </BottomTextContainer>
                )}
            </Square>
        </Link>
    )
}

const Square = styled.div`
    position: absolute;
    width: calc(100% - 16px);
    height: calc(100% - 16px);
    background: ${props =>
        props.backgroundColor ? props.backgroundColor : 'white'};
    margin: 8px;
    border-radius: 10px;
    box-sizing: border-box;

    ${H6}, ${Text} {
        text-align: left;
    }

    ${media.medium`
        margin: ${props => (props.margin ? props.margin : '0')};
    `};
`

const text = css`
    position: absolute;
    z-index: 2;
    left: 15px;
    ${media.medium`
         left: 30px;
    `};
`

const top = css`
    top: 10px;
    ${media.small`
        top: 20px;
    `}
`

const bottom = css`
    bottom: 5px;
    ${media.medium`
        bottom: 30px;
    `};
`

const SquareContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 10%;
`

const ImageContainer = styled.div`
    font-size: 0;
    width: 60%;
    height: 60%;
    ${props => props.hasTileText && `padding-top: 40px;`}
`

const StyledH6 = styled(H6)`
    ${text};
    ${top};
    text-align: ${props => (props.isCategory ? 'center' : 'left')};
    margin-top: 0;
    font-family: ${fonts.flameRegular};
    font-size: 16px;
    display: flex;
    justify-content: ${props => (props.isCategory ? 'center' : 'flex-start')};
    left: 10px !important;
    width: calc(100% - 20px);

    ${media.medium`
        font-size: 21px;
    `};
    font-stretch: condensed;
    color: ${props => (props.color ? props.color : colors.text)};
`

const StyledText = styled(Text)`
    ${text};
    ${bottom};
    color: ${colors.grey};
`

const BottomTextContainer = styled.div`
    ${text};
    text-align: center;
    transform: translateX(-50%);
    top: calc(100% - 60px);
    width: calc(100% - 20px);
    left: 50%;
    ${media.medium`
         width: calc(100% - 30px);
         left: 50%;
    `};
`

const icon = css`
    position: absolute;
    z-index: 2;
    right: 15px;
    ${media.medium`
         right: 30px;
    `};
`

const IconTop = styled.span`
    ${icon};
    ${top};
`
const IconBottom = styled.span`
    ${icon};
    ${bottom};
`

export { TileSquare }
